import {
  postV1CompaniesCompanyIdLoadDigestPdfs,
  postV1CompaniesCompanyIdOrdersDispatchLoadDigestPdfs,
  postV1CompaniesCompanyIdOrdersOrderIdLoadDigestPdfs,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import useRenderedPdfDownload from '~hooks/useRenderedPdfDownload/useRenderedPdfDownload';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  LoadDigestLoadFilter,
  toLoadFilterBody,
  withJobLoadFilter,
} from '../useLoadDigests/models';
import buildDispatchSearchRequest from '../useOrders/buildDispatchSearchRequest';

// Outlasts the 240-second render lambda limit, which a 250-load digest can reach.
const LOAD_DIGEST_RENDER_TIMEOUT_IN_MS = 5 * 60 * 1000;

export default function useLoadDigestPdf() {
  const { ordersDispatchStore, userStore } = useStores();
  const { downloadRenderedPdf } = useRenderedPdfDownload();

  const viewForOrder = useCallback(
    (orderId: string, loadFilter: LoadDigestLoadFilter) =>
      downloadRenderedPdf({
        failureMessage: t('dispatch.load_digest.generation_failed'),
        render: () =>
          postV1CompaniesCompanyIdOrdersOrderIdLoadDigestPdfs({
            path: {
              'company-id': getEffectiveUserCompanyId(userStore),
              'order-id': orderId,
            },
            body: toLoadFilterBody(loadFilter),
          }),
        timeoutMs: LOAD_DIGEST_RENDER_TIMEOUT_IN_MS,
      }),
    [downloadRenderedPdf, userStore],
  );

  const viewForJobs = useCallback(
    (jobIds: string[], loadFilter: LoadDigestLoadFilter) =>
      downloadRenderedPdf({
        failureMessage: t('dispatch.load_digest.generation_failed'),
        render: () =>
          postV1CompaniesCompanyIdLoadDigestPdfs({
            path: { 'company-id': getEffectiveUserCompanyId(userStore) },
            body: { job_ids: jobIds, ...toLoadFilterBody(loadFilter) },
          }),
        timeoutMs: LOAD_DIGEST_RENDER_TIMEOUT_IN_MS,
      }),
    [downloadRenderedPdf, userStore],
  );

  const viewForDispatch = useCallback(
    (loadFilter: LoadDigestLoadFilter) =>
      downloadRenderedPdf({
        failureMessage: t('dispatch.load_digest.generation_failed'),
        render: () =>
          postV1CompaniesCompanyIdOrdersDispatchLoadDigestPdfs({
            path: { 'company-id': getEffectiveUserCompanyId(userStore) },
            body: withJobLoadFilter(
              buildDispatchSearchRequest(ordersDispatchStore.filters, {
                includeDates: true,
              }),
              loadFilter,
            ),
          }),
        timeoutMs: LOAD_DIGEST_RENDER_TIMEOUT_IN_MS,
      }),
    [downloadRenderedPdf, ordersDispatchStore, userStore],
  );

  return { viewForDispatch, viewForJobs, viewForOrder };
}
